import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useTreeA11yContext } from '../components/TreeA11yContext.js';
import { TREE_DRAG_TYPE } from '../constants.js';
import { dnd } from '../Tree.js';
import { isTreeDropNodeValid, canDraggedNodeBeDroppedOnTopBar, canDraggedNodeBeDroppedOnBottomBar } from '../utils.js';

var useTreeNodeDragDrop = function useTreeNodeDragDrop(props) {
  var _useTreeNodeDrag = useTreeNodeDrag(props),
    canDragNode = _useTreeNodeDrag.canDragNode,
    connectDragPreview = _useTreeNodeDrag.connectDragPreview,
    connectDragSource = _useTreeNodeDrag.connectDragSource,
    isMouseDragging = _useTreeNodeDrag.isMouseDragging;
  var _useTreeNodeOverDrop = useTreeNodeOverDrop(props),
    canDropNode = _useTreeNodeOverDrop.canDropNode,
    connectDropTarget = _useTreeNodeOverDrop.connectDropTarget,
    isMouseDragOverNode = _useTreeNodeOverDrop.isMouseDragOverNode;
  var _useTreeNodeTopBarDro = useTreeNodeTopBarDrop(props),
    canDropTopBar = _useTreeNodeTopBarDro.canDropTopBar,
    connectDropTopBarTarget = _useTreeNodeTopBarDro.connectDropTopBarTarget,
    isMouseDragOverTopBar = _useTreeNodeTopBarDro.isMouseDragOverTopBar;
  var _useTreeNodeBottomBar = useTreeNodeBottomBarDrop(props),
    canDropBottomBar = _useTreeNodeBottomBar.canDropBottomBar,
    connectDropBottomBarTarget = _useTreeNodeBottomBar.connectDropBottomBarTarget,
    isMouseDragOverBottomBar = _useTreeNodeBottomBar.isMouseDragOverBottomBar;
  var _useA11yDragDrop = useA11yDragDrop(props),
    canA11yDropBottomBar = _useA11yDragDrop.canA11yDropBottomBar,
    canA11yDropOverNode = _useA11yDragDrop.canA11yDropOverNode,
    canA11yDropTopBar = _useA11yDragDrop.canA11yDropTopBar,
    isA11yDragging = _useA11yDragDrop.isA11yDragging,
    isA11yDragOverNode = _useA11yDragDrop.isA11yDragOverNode;
  var isDragging = isMouseDragging || isA11yDragging;
  var isDragOverTopBar = canDropTopBar && isMouseDragOverTopBar || canA11yDropTopBar;
  var isDragOverBottomBar = canDropBottomBar && isMouseDragOverBottomBar || canA11yDropBottomBar;
  var isDragOverNode = canDropNode && isMouseDragOverNode || canA11yDropOverNode && isA11yDragOverNode;
  var isTopDropBarVisible = canDropTopBar || canA11yDropTopBar;
  var isBottomDropBarVisible = (canDropBottomBar || canA11yDropBottomBar) && props.context.isLastChild() && !props.context.isExpanded();
  return {
    canDragNode: canDragNode,
    connectDragSource: connectDragSource,
    connectDragPreview: connectDragPreview,
    connectDropTarget: connectDropTarget,
    isDragging: isDragging,
    isMouseDragging: isMouseDragging,
    isDragOverNode: isDragOverNode,
    isDragOverTopBar: isDragOverTopBar,
    isDragOverBottomBar: isDragOverBottomBar,
    isTopDropBarVisible: isTopDropBarVisible,
    isBottomDropBarVisible: isBottomDropBarVisible,
    connectDropTopBarTarget: connectDropTopBarTarget,
    connectDropBottomBarTarget: connectDropBottomBarTarget
  };
};
function useTreeNodeDrag(props) {
  var a11yContext = useTreeA11yContext();
  var _dnd$useDrag = dnd.useDrag(function () {
      return {
        canDrag: function canDrag() {
          return !!props.onChange && !a11yContext.draggedNodeId && props.canDrag({
            context: props.context,
            node: props.node
          });
        },
        collect: function collect(monitor) {
          return {
            canDragNode: monitor.canDrag(),
            isMouseDragging: monitor.isDragging()
          };
        },
        item: function item() {
          if (props.onDragStart) {
            props.onDragStart({
              level: props.context.getAriaLevel()
            });
          }
          if (props.context.isExpanded()) {
            props.onCollapse();
          }
          return {
            context: props.context,
            node: props.node
          };
        },
        type: TREE_DRAG_TYPE
      };
    }, [props.context]),
    _dnd$useDrag2 = _slicedToArray(_dnd$useDrag, 3),
    _dnd$useDrag2$ = _dnd$useDrag2[0],
    canDragNode = _dnd$useDrag2$.canDragNode,
    isMouseDragging = _dnd$useDrag2$.isMouseDragging,
    connectDragSource = _dnd$useDrag2[1],
    connectDragPreview = _dnd$useDrag2[2];
  return {
    canDragNode: canDragNode,
    isMouseDragging: isMouseDragging,
    connectDragSource: connectDragSource,
    connectDragPreview: connectDragPreview
  };
}
function useTreeNodeOverDrop(props) {
  var _dnd$useDrop = dnd.useDrop(function () {
      return {
        accept: TREE_DRAG_TYPE,
        canDrop: function canDrop(source) {
          return isTreeDropNodeValid({
            canDrop: props.canDrop,
            sourceNodeContext: source.context,
            target: {
              context: props.context,
              node: props.node
            }
          });
        },
        drop: function drop(source, monitor) {
          if (monitor.didDrop()) {
            return;
          }
          props.onChange({
            position: "childof",
            source: {
              context: source.context,
              node: source.node
            },
            target: {
              context: props.context,
              node: props.node
            }
          });
        },
        collect: function collect(monitor) {
          return {
            canDropNode: monitor.canDrop(),
            isMouseDragOverNode: monitor.isOver()
          };
        }
      };
    }, [props.context]),
    _dnd$useDrop2 = _slicedToArray(_dnd$useDrop, 2),
    _dnd$useDrop2$ = _dnd$useDrop2[0],
    canDropNode = _dnd$useDrop2$.canDropNode,
    isMouseDragOverNode = _dnd$useDrop2$.isMouseDragOverNode,
    connectDropTarget = _dnd$useDrop2[1];
  return {
    canDropNode: canDropNode,
    isMouseDragOverNode: isMouseDragOverNode,
    connectDropTarget: connectDropTarget
  };
}
function useTreeNodeTopBarDrop(props) {
  var _dnd$useDrop3 = dnd.useDrop(function () {
      return {
        accept: TREE_DRAG_TYPE,
        canDrop: function canDrop(source) {
          return props.canOrder !== void 0 && canDraggedNodeBeDroppedOnTopBar(source.context, props.context, props.canDrop);
        },
        drop: function drop(source) {
          props.onChange({
            position: "before",
            source: {
              context: source.context,
              node: source.node
            },
            target: {
              context: props.context,
              node: props.node
            }
          });
        },
        collect: function collect(monitor) {
          return {
            canDropTopBar: monitor.canDrop(),
            isMouseDragOverTopBar: monitor.isOver()
          };
        }
      };
    }, [props.context]),
    _dnd$useDrop4 = _slicedToArray(_dnd$useDrop3, 2),
    _dnd$useDrop4$ = _dnd$useDrop4[0],
    canDropTopBar = _dnd$useDrop4$.canDropTopBar,
    isMouseDragOverTopBar = _dnd$useDrop4$.isMouseDragOverTopBar,
    connectDropTopBarTarget = _dnd$useDrop4[1];
  return {
    canDropTopBar: canDropTopBar,
    isMouseDragOverTopBar: isMouseDragOverTopBar,
    connectDropTopBarTarget: connectDropTopBarTarget
  };
}
function useTreeNodeBottomBarDrop(props) {
  var _dnd$useDrop5 = dnd.useDrop(function () {
      return {
        accept: TREE_DRAG_TYPE,
        canDrop: function canDrop(source) {
          return props.canOrder !== void 0 && canDraggedNodeBeDroppedOnBottomBar(source.context, props.context, props.canDrop);
        },
        drop: function drop(source) {
          props.onChange({
            position: "after",
            source: {
              context: source.context,
              node: source.node
            },
            target: {
              context: props.context,
              node: props.node
            }
          });
        },
        collect: function collect(monitor) {
          return {
            canDropBottomBar: monitor.canDrop(),
            isMouseDragOverBottomBar: monitor.isOver()
          };
        }
      };
    }, [props.context]),
    _dnd$useDrop6 = _slicedToArray(_dnd$useDrop5, 2),
    _dnd$useDrop6$ = _dnd$useDrop6[0],
    canDropBottomBar = _dnd$useDrop6$.canDropBottomBar,
    isMouseDragOverBottomBar = _dnd$useDrop6$.isMouseDragOverBottomBar,
    connectDropBottomBarTarget = _dnd$useDrop6[1];
  return {
    canDropBottomBar: canDropBottomBar,
    isMouseDragOverBottomBar: isMouseDragOverBottomBar,
    connectDropBottomBarTarget: connectDropBottomBarTarget
  };
}
function useA11yDragDrop(props) {
  var a11yContext = useTreeA11yContext();
  var isA11yFocusedOverNode = a11yContext.focusedNodeId === props.context.id;
  var isA11yDragging = a11yContext.draggedNodeId === props.context.id;
  var isA11yDragOverNode = isA11yFocusedOverNode && a11yContext.focusedDropRegion === "over";
  var isA11yDragOverTopBar = isA11yFocusedOverNode && a11yContext.focusedDropRegion === "top";
  var isA11yDragOverBottomBar = isA11yFocusedOverNode && a11yContext.focusedDropRegion === "bottom";
  var canA11yDropOverNode = props.canDrop(props.context.parentId ? {
    context: props.context,
    node: props.node
  } : void 0);
  var canA11yDropTopAndBottomBar = props.canDrop(props.context.parentId ? {
    context: props.context.getParentContext(),
    node: props.context.getParentNode()
  } : void 0);
  return {
    canA11yDropTopBar: canA11yDropTopAndBottomBar && isA11yDragOverTopBar,
    canA11yDropBottomBar: canA11yDropTopAndBottomBar && isA11yDragOverBottomBar,
    canA11yDropOverNode: canA11yDropOverNode,
    isA11yDragging: isA11yDragging,
    isA11yDragOverNode: isA11yDragOverNode
  };
}

export { useTreeNodeDragDrop };
